import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';

import { multiShiftOutreachMax } from '@apps/web/components/outreaches/edit_shifts_section';
import { type ChatAppearance } from '@apps/web/components/phoebe_chat/chat_appearance';
import {
    ReferenceChip,
    ReferenceChipTray,
    ReferenceRemoveButton,
} from '@apps/web/components/phoebe_chat/reference_chip';
import { formatCompactTimeRange } from '@apps/web/components/shifts_calendar/shift_chip';
import { Button } from '@apps/web/components/ui/button';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    chatReferenceStore,
    type StagedReference,
} from '@apps/web/lib/chat_reference_store';
import {
    formatDate,
    formatDateWithWeekday,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { type Shift } from '@libraries/typescript/api_client';

const SUMMARY_THRESHOLD = 4;
const MAX_STACK_AVATARS = 3;

type ClientGroup = {
    readonly key: string;
    readonly name: string;
    readonly shifts: Array<Shift>;
};

function clientDisplayName(shift: Shift): string {
    return shift.patient_name ?? 'Unknown client';
}

function groupShiftsByClient(shifts: ReadonlyArray<Shift>): Array<ClientGroup> {
    const groups = new Map<string, ClientGroup>();
    for (const shift of shifts) {
        const key = shift.client_id ?? clientDisplayName(shift);
        const group = groups.get(key);
        if (group) {
            group.shifts.push(shift);
        } else {
            groups.set(key, {
                key,
                name: clientDisplayName(shift),
                shifts: [shift],
            });
        }
    }
    return [...groups.values()];
}

function formatSelectionDateRange(
    shifts: ReadonlyArray<Shift>,
    timezone: string,
): string {
    const first = shifts[0];
    const last = shifts[shifts.length - 1];
    if (!first || !last) {
        return '';
    }
    const startLabel = formatDate(first.shift_start_time, timezone);
    const startKey = toDateKey(new Date(first.shift_start_time), timezone);
    const endKey = toDateKey(new Date(last.shift_start_time), timezone);
    if (startKey === endKey) {
        return startLabel;
    }
    const sameMonth = startKey.slice(0, 7) === endKey.slice(0, 7);
    return sameMonth
        ? `${startLabel} – ${Number(endKey.slice(8))}`
        : `${startLabel} – ${formatDate(last.shift_start_time, timezone)}`;
}

function ClientAvatarStack({
    groups,
}: {
    readonly groups: ReadonlyArray<ClientGroup>;
}) {
    const overflowCount =
        groups.length > MAX_STACK_AVATARS
            ? groups.length - (MAX_STACK_AVATARS - 1)
            : 0;
    const visibleGroups =
        overflowCount > 0 ? groups.slice(0, MAX_STACK_AVATARS - 1) : groups;
    return (
        <div className="flex shrink-0 -space-x-1.5">
            {visibleGroups.map((group) => (
                <UserAvatar
                    key={group.key}
                    displayName={group.name}
                    colorKey={group.key}
                    className="ring-background size-6 ring-2"
                />
            ))}
            {overflowCount > 0 ? (
                <span className="bg-muted text-muted-foreground ring-background flex size-6 items-center justify-center rounded-full text-[10px] font-medium ring-2">
                    {`+${overflowCount}`}
                </span>
            ) : null}
        </div>
    );
}

export function ShiftReferenceSection({
    references,
    appearance = 'default',
}: {
    readonly references: ReadonlyArray<StagedReference>;
    readonly appearance?: ChatAppearance;
}) {
    const { timezone } = useOrganizationTimezone();
    const [isExpanded, setIsExpanded] = useState(false);
    const shifts = references
        .filter((reference) => reference.type === 'shift')
        .map((reference) => reference.shift);

    if (shifts.length < SUMMARY_THRESHOLD) {
        return (
            <ReferenceChipTray
                appearance={appearance}
                testId="shift-reference-tray"
            >
                {shifts.map((shift) => (
                    <ReferenceChip
                        key={shift.id}
                        appearance={appearance}
                        leading={
                            <UserAvatar
                                displayName={clientDisplayName(shift)}
                                colorKey={shift.client_id ?? undefined}
                                className="size-5 shrink-0"
                            />
                        }
                        hideLeadingInV3={true}
                        removeLabel={`Remove ${clientDisplayName(shift)} shift from message`}
                        removeTestId="shift-reference-remove"
                        rowTestId="shift-reference-row"
                        onRemove={() => chatReferenceStore.remove(shift.id)}
                    >
                        <span className="min-w-0 flex-1 truncate text-xs leading-4">
                            <span className="font-medium">
                                {clientDisplayName(shift)}
                            </span>
                            <span className="text-muted-foreground">
                                {` · ${formatDateWithWeekday(
                                    shift.shift_start_time,
                                    timezone,
                                )} · `}
                            </span>
                            <span className="text-foreground font-medium">
                                {formatCompactTimeRange(shift, timezone)}
                            </span>
                        </span>
                    </ReferenceChip>
                ))}
            </ReferenceChipTray>
        );
    }

    const clientGroups = groupShiftsByClient(shifts);
    const selectionLimit = multiShiftOutreachMax(
        shifts.map((shift) => shift.client_id ?? null),
    );
    const isAtCap = shifts.length >= selectionLimit;
    const summaryTitle =
        clientGroups.length === 1
            ? `${shifts.length} shifts for ${clientGroups[0]?.name}`
            : `${shifts.length} shifts across ${clientGroups.length} clients`;
    const summarySubtitle = `${formatSelectionDateRange(
        shifts,
        timezone,
    )}${isAtCap ? ` · max ${selectionLimit}` : ''}`;
    const ChevronIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
        <div
            data-testid="shift-reference-tray"
            className="border-border bg-background mb-2 rounded-xl border"
        >
            <div
                data-testid="shift-reference-summary"
                className="flex items-center gap-2.5 px-2.5 py-2"
            >
                <ClientAvatarStack groups={clientGroups} />
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={
                        isExpanded
                            ? 'Collapse selected shifts'
                            : 'Expand selected shifts'
                    }
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => setIsExpanded((previous) => !previous)}
                >
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                            {summaryTitle}
                        </span>
                        <span className="text-muted-foreground block truncate text-xs">
                            {summarySubtitle}
                        </span>
                    </span>
                    <ChevronIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                </button>
                <button
                    type="button"
                    aria-label="Clear selected shifts"
                    data-testid="shift-reference-clear"
                    className="text-muted-foreground hover:text-foreground flex h-5 w-5 shrink-0 items-center justify-center rounded"
                    onClick={(event) => {
                        event.stopPropagation();
                        chatReferenceStore.clearType('shift');
                    }}
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
            {isExpanded ? (
                <div className="border-border border-t">
                    <div className="max-h-64 space-y-3 overflow-y-auto p-2">
                        {clientGroups.map((group) => (
                            <div key={group.key}>
                                <div className="flex items-center gap-2 px-0.5 pb-1">
                                    <UserAvatar
                                        displayName={group.name}
                                        colorKey={group.key}
                                        className="size-5 shrink-0"
                                    />
                                    <span className="min-w-0 flex-1 truncate text-xs font-semibold">
                                        {group.name}
                                    </span>
                                    <span className="bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium">
                                        {group.shifts.length}
                                    </span>
                                </div>
                                <div className="space-y-1">
                                    {group.shifts.map((shift) => (
                                        <div
                                            key={shift.id}
                                            data-testid="shift-reference-row"
                                            className="bg-muted/40 hover:bg-muted/70 flex items-center gap-2 rounded-lg px-2 py-1"
                                        >
                                            <span className="min-w-0 flex-1 truncate text-xs">
                                                <span className="text-muted-foreground">
                                                    {`${formatDateWithWeekday(
                                                        shift.shift_start_time,
                                                        timezone,
                                                    )} `}
                                                </span>
                                                <span className="text-foreground font-medium">
                                                    {formatCompactTimeRange(
                                                        shift,
                                                        timezone,
                                                    )}
                                                </span>
                                            </span>
                                            <ReferenceRemoveButton
                                                appearance="default"
                                                label={`Remove ${clientDisplayName(shift)} shift from message`}
                                                testId="shift-reference-remove"
                                                onClick={() =>
                                                    chatReferenceStore.remove(
                                                        shift.id,
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="border-border border-t p-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() =>
                                chatReferenceStore.clearType('shift')
                            }
                        >
                            Clear all
                        </Button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
